import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, onSnapshot, setDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { ChevronLeft, ChevronRight, Save, Trash2, Calendar, FileText, Clock, FilePlus } from 'lucide-react';
import PageHeader from './common/PageHeader';

function CalendarView({ user }) {
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [orders, setOrders] = useState([]);
    const [partners, setPartners] = useState({});
    const [memos, setMemos] = useState({});
    const [selectedDate, setSelectedDate] = useState(new Date()); // 기본값: 오늘
    const [editingMemo, setEditingMemo] = useState(null);
    const [memoText, setMemoText] = useState('');
    const [loading, setLoading] = useState(true);

    const targetUserId = user.companyId || user.uid;

    useEffect(() => {
        if (!user) return;
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;

        const memosQuery = query(
            collection(db, 'calendarMemos'),
            where('userId', '==', targetUserId),
            where('year', '==', year),
            where('month', '==', month)
        );

        const unsubscribeMemos = onSnapshot(memosQuery, (snapshot) => {
            const memosData = {};
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                const key = `${data.year}-${data.month}-${data.day}`;
                memosData[key] = { id: doc.id, ...data };
            });
            setMemos(memosData);
        });

        let isFirstSentSnapshot = true;
        let isFirstReceivedSnapshot = true;

        const sentOrdersQuery = query(collection(db, 'workOrders'), where('fromUserId', '==', targetUserId));
        const unsubscribeSent = onSnapshot(sentOrdersQuery, () => {
            if (isFirstSentSnapshot) { isFirstSentSnapshot = false; return; }
            fetchMonthData();
        });

        const receivedOrdersQuery = query(collection(db, 'workOrders'), where('toUserId', '==', targetUserId));
        const unsubscribeReceived = onSnapshot(receivedOrdersQuery, () => {
            if (isFirstReceivedSnapshot) { isFirstReceivedSnapshot = false; return; }
            fetchMonthData();
        });

        fetchMonthData();

        return () => { unsubscribeMemos(); unsubscribeSent(); unsubscribeReceived(); };
        // eslint-disable-next-line
    }, [currentDate.getFullYear(), currentDate.getMonth(), user?.uid, user?.companyId]);

    // 선택된 날짜가 바뀌면 메모 로드
    useEffect(() => {
        if (selectedDate) {
            const key = `${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}-${selectedDate.getDate()}`;
            const existingMemo = memos[key];
            if (existingMemo) {
                setEditingMemo(existingMemo);
                setMemoText(existingMemo.memo);
            } else {
                setEditingMemo(null);
                setMemoText('');
            }
        }
    }, [selectedDate, memos]);

    const fetchPartnerInfo = async (partnerId) => {
        if (!partnerId) return { name: '알 수 없음' };
        if (partners[partnerId]) return partners[partnerId];
        try {
            const partnerDoc = await getDoc(doc(db, 'users', partnerId));
            if (partnerDoc.exists()) {
                const partnerData = partnerDoc.data();
                const partnerInfo = { name: partnerData.businessName || partnerData.displayName || '알 수 없음' };
                setPartners(prev => ({ ...prev, [partnerId]: partnerInfo }));
                return partnerInfo;
            }
        } catch (error) { console.error('거래처 정보 가져오기 실패:', error); }
        return { name: '알 수 없음' };
    };

    const fetchMonthData = async () => {
        try {
            setLoading(true);
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const startOfMonth = new Date(year, month, 1);
            const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59);

            const [sentSnapshot, receivedSnapshot] = await Promise.all([
                getDocs(query(collection(db, 'workOrders'), where('fromUserId', '==', targetUserId))),
                getDocs(query(collection(db, 'workOrders'), where('toUserId', '==', targetUserId)))
            ]);

            const userOrders = [
                ...sentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
                ...receivedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
            ];

            const monthOrders = userOrders.filter(order => {
                const dateField = order.dueDate || order.deliveryDate || order.completionDate;
                if (!dateField) return false;
                const orderDate = dateField.toDate ? dateField.toDate() : new Date(dateField);
                return orderDate >= startOfMonth && orderDate <= endOfMonth;
            });

            setOrders(monthOrders.map(order => ({
                ...order,
                dueDate: order.dueDate || order.deliveryDate || order.completionDate
            })));

            const partnerIds = [...new Set(monthOrders.map(o => o.fromUserId === targetUserId ? o.toUserId : o.fromUserId))];
            for (const partnerId of partnerIds) { await fetchPartnerInfo(partnerId); }
        } catch (error) { console.error('데이터 가져오기 실패:', error); }
        finally { setLoading(false); }
    };

    const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    const goToday = () => { setCurrentDate(new Date()); setSelectedDate(new Date()); };

    const handleDateClick = (date) => {
        setSelectedDate(date);
    };

    const saveMemo = async () => {
        if (!selectedDate || !memoText.trim()) { alert('메모를 입력해주세요.'); return; }
        try {
            const year = selectedDate.getFullYear();
            const month = selectedDate.getMonth() + 1;
            const day = selectedDate.getDate();
            const memoId = `${targetUserId}_${year}_${month}_${day}`;

            await setDoc(doc(db, 'calendarMemos', memoId), {
                userId: targetUserId,
                createdBy: user.uid,
                createdByName: user.name || user.email,
                year, month, day,
                memo: memoText.trim(),
                createdAt: new Date(),
                updatedAt: new Date()
            });
            alert('메모가 저장되었습니다.');
        } catch (error) { console.error('메모 저장 실패:', error); alert('메모 저장에 실패했습니다.'); }
    };

    const deleteMemo = async () => {
        if (!editingMemo || !selectedDate) return;
        if (!window.confirm('메모를 삭제하시겠습니까?')) return;
        try {
            const year = selectedDate.getFullYear();
            const month = selectedDate.getMonth() + 1;
            const day = selectedDate.getDate();
            await deleteDoc(doc(db, 'calendarMemos', `${targetUserId}_${year}_${month}_${day}`));
            setEditingMemo(null);
            setMemoText('');
            alert('메모가 삭제되었습니다.');
        } catch (error) { console.error('메모 삭제 실패:', error); alert('메모 삭제에 실패했습니다.'); }
    };

    const getDaysInMonth = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];
        for (let i = 0; i < firstDay.getDay(); i++) { days.push(null); }
        for (let day = 1; day <= lastDay.getDate(); day++) { days.push(new Date(year, month, day)); }
        return days;
    };

    const getOrdersForDate = (date, type) => {
        if (!date) return [];
        const dateStr = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
        return orders.filter(order => {
            const targetDate = type === 'created' ? order.createdAt : order.dueDate;
            if (!targetDate) return false;
            const orderDate = targetDate.toDate ? targetDate.toDate() : new Date(targetDate);
            return `${orderDate.getFullYear()}-${orderDate.getMonth()}-${orderDate.getDate()}` === dateStr;
        });
    };

    const isToday = (date) => {
        if (!date) return false;
        const today = new Date();
        return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    };

    const isSelected = (date) => {
        if (!date || !selectedDate) return false;
        return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
    };

    const hasMemo = (date) => {
        if (!date) return false;
        return !!memos[`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`];
    };

    const hasOrders = (date) => {
        if (!date) return false;
        return getOrdersForDate(date, 'due').length > 0;
    };

    const renderOrderItem = (order, idx) => {
        const isReceiver = order.toUserId === targetUserId;
        const partnerId = isReceiver ? order.fromUserId : order.toUserId;
        const partnerInfo = partners[partnerId] || { name: '알 수 없음' };
        return (
            <div key={'order-' + idx + '-' + order.id} style={styles.orderItem} onClick={() => navigate(`/view-order/${order.id}`)}>
                <div style={styles.orderInfo}>
                    <div style={styles.patientName}>{order.patientName || '환자명 없음'}</div>
                    <div style={styles.companyName}>{partnerInfo.name}</div>
                </div>
                <ChevronRight size={16} color="#9ca3af" />
            </div>
        );
    };

    const days = getDaysInMonth();
    const createdOrders = selectedDate ? getOrdersForDate(selectedDate, 'created') : [];
    const dueOrders = selectedDate ? getOrdersForDate(selectedDate, 'due') : [];

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <div style={styles.loadingText}>데이터 불러오는 중...</div>
                <style>{spinKeyframes}</style>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <PageHeader icon={Calendar} title="캘린더" />
            <div style={styles.splitLayout}>
                {/* 왼쪽: 달력 */}
                <div style={styles.leftPanel}>
                    {/* 월 네비게이션 */}
                    <div style={styles.monthNav}>
                        <button onClick={prevMonth} style={styles.navButton}><ChevronLeft size={18} /></button>
                        <span style={styles.monthTitle}>{currentDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })}</span>
                        <button onClick={nextMonth} style={styles.navButton}><ChevronRight size={18} /></button>
                        <button onClick={goToday} style={styles.todayButton}>오늘</button>
                    </div>

                    {/* 캘린더 */}
                    <div style={styles.calendarCard}>
                        <div style={styles.weekHeader}>
                            {['일', '월', '화', '수', '목', '금', '토'].map((day, index) => (
                                <div key={day} style={{ ...styles.weekDay, color: index === 0 ? '#ef4444' : index === 6 ? '#3b82f6' : '#64748b' }}>{day}</div>
                            ))}
                        </div>
                        <div style={styles.daysGrid}>
                            {days.map((date, index) => {
                                if (!date) return <div key={`empty-${index}`} style={styles.emptyDayCell} />;
                                const selected = isSelected(date);
                                const today = isToday(date);
                                return (
                                    <div
                                        key={date.toISOString()}
                                        style={{
                                            ...styles.dayCell,
                                            ...(today ? styles.todayCell : {}),
                                            ...(selected ? styles.selectedCell : {})
                                        }}
                                        onClick={() => handleDateClick(date)}
                                    >
                                        <span style={{
                                            ...styles.dayText,
                                            color: selected ? '#fff' : today ? '#6366f1' : date.getDay() === 0 ? '#ef4444' : date.getDay() === 6 ? '#3b82f6' : '#374151',
                                            fontWeight: today || selected ? '700' : '500'
                                        }}>
                                            {date.getDate()}
                                        </span>
                                        <div style={styles.dotsContainer}>
                                            {hasMemo(date) && <div style={{ ...styles.dot, backgroundColor: '#f59e0b' }} />}
                                            {hasOrders(date) && <div style={{ ...styles.dot, backgroundColor: '#6366f1' }} />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={styles.legend}>
                            <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#6366f1' }} /><span style={styles.legendText}>마감일</span></div>
                            <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#f59e0b' }} /><span style={styles.legendText}>메모</span></div>
                        </div>
                    </div>
                </div>

                {/* 오른쪽: 상세 패널 */}
                <div style={styles.rightPanel}>
                    {/* 선택된 날짜 헤더 */}
                    <div style={styles.dateHeader}>
                        <Calendar size={20} color="#6366f1" />
                        <span style={styles.dateTitle}>
                            {selectedDate ? selectedDate.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' }) : '날짜를 선택하세요'}
                        </span>
                    </div>

                    {/* 메모 섹션 */}
                    <div style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <div style={{ ...styles.iconBox, backgroundColor: '#fef3c7' }}><FileText size={16} color="#f59e0b" /></div>
                            <span style={styles.sectionTitle}>메모</span>
                        </div>
                        <textarea
                            style={styles.memoInput}
                            placeholder="메모를 입력하세요..."
                            value={memoText}
                            onChange={(e) => setMemoText(e.target.value)}
                        />
                        <div style={styles.memoButtons}>
                            {editingMemo && (
                                <button style={styles.deleteButton} onClick={deleteMemo}>
                                    <Trash2 size={14} /><span>삭제</span>
                                </button>
                            )}
                            <button style={styles.saveButton} onClick={saveMemo}>
                                <Save size={14} /><span>저장</span>
                            </button>
                        </div>
                    </div>

                    {/* 작성일 섹션 */}
                    <div style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <div style={{ ...styles.iconBox, backgroundColor: '#dcfce7' }}><FilePlus size={16} color="#10b981" /></div>
                            <span style={styles.sectionTitle}>작성일</span>
                            <span style={styles.countBadge}>{createdOrders.length}</span>
                        </div>
                        <div style={styles.orderList}>
                            {createdOrders.length > 0 ? createdOrders.map((o, i) => renderOrderItem(o, i)) : <div style={styles.emptyText}>해당 날짜에 작성된 주문이 없습니다</div>}
                        </div>
                    </div>

                    {/* 마감일 섹션 */}
                    <div style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <div style={{ ...styles.iconBox, backgroundColor: '#fee2e2' }}><Clock size={16} color="#ef4444" /></div>
                            <span style={styles.sectionTitle}>마감일</span>
                            <span style={styles.countBadge}>{dueOrders.length}</span>
                        </div>
                        <div style={styles.orderList}>
                            {dueOrders.length > 0 ? dueOrders.map((o, i) => renderOrderItem(o, i)) : <div style={styles.emptyText}>해당 날짜에 마감인 주문이 없습니다</div>}
                        </div>
                    </div>
                </div>
            </div>
            <style>{spinKeyframes}</style>
        </div>
    );
}

const spinKeyframes = '@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';

const styles = {
    container: { minHeight: '100vh', backgroundColor: '#f1f5f9' },
    
    splitLayout: { display: 'flex', minHeight: '100vh' },
    
    // 왼쪽 패널 (달력)
    leftPanel: { flex: 1, padding: 20, borderRight: '1px solid #e2e8f0', backgroundColor: '#fff', maxWidth: '50%' },
    
    // 오른쪽 패널 (상세)
    rightPanel: { flex: 1, padding: 20, backgroundColor: '#f8fafc', overflowY: 'auto', maxWidth: '50%' },
    
    // 월 네비게이션
    monthNav: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 16 },
    monthTitle: { fontSize: 16, fontWeight: '700', color: '#0f172a', minWidth: 140, textAlign: 'center' },
    navButton: { width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#64748b' },
    todayButton: { padding: '6px 12px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: '600', cursor: 'pointer' },
    
    // 캘린더
    calendarCard: { backgroundColor: '#fff', borderRadius: 12, padding: 12 },
    weekHeader: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 8 },
    weekDay: { textAlign: 'center', padding: 8, fontSize: 12, fontWeight: '600' },
    daysGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 },
    emptyDayCell: { aspectRatio: '1' },
    dayCell: { aspectRatio: '1', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s' },
    todayCell: { backgroundColor: '#eef2ff', border: '2px solid #6366f1' },
    selectedCell: { backgroundColor: '#6366f1' },
    dayText: { fontSize: 13 },
    dotsContainer: { display: 'flex', gap: 2, marginTop: 2 },
    dot: { width: 4, height: 4, borderRadius: '50%' },
    legend: { display: 'flex', justifyContent: 'center', padding: '12px 0', gap: 16 },
    legendItem: { display: 'flex', alignItems: 'center', gap: 4 },
    legendDot: { width: 6, height: 6, borderRadius: 3 },
    legendText: { fontSize: 11, color: '#64748b' },
    
    // 오른쪽 패널 요소
    dateHeader: { display: 'flex', alignItems: 'center', gap: 10, padding: 16, backgroundColor: '#fff', borderRadius: 12, marginBottom: 16 },
    dateTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a' },
    
    section: { backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 16 },
    sectionHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
    iconBox: { width: 32, height: 32, borderRadius: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' },
    sectionTitle: { fontSize: 14, fontWeight: '700', color: '#0f172a', flex: 1 },
    countBadge: { backgroundColor: '#eef2ff', color: '#6366f1', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: '600' },
    
    // 메모
    memoInput: { width: '100%', minHeight: 80, padding: 12, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none' },
    memoButtons: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 },
    deleteButton: { display: 'flex', alignItems: 'center', gap: 4, padding: '8px 12px', backgroundColor: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: '600', cursor: 'pointer' },
    saveButton: { display: 'flex', alignItems: 'center', gap: 4, padding: '8px 12px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: '600', cursor: 'pointer' },
    
    // 주문 리스트
    orderList: { maxHeight: 200, overflowY: 'auto' },
    orderItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 10, backgroundColor: '#f8fafc', borderRadius: 8, marginBottom: 6, cursor: 'pointer' },
    orderInfo: { flex: 1 },
    patientName: { fontSize: 13, fontWeight: '600', color: '#0f172a', marginBottom: 2 },
    companyName: { fontSize: 11, color: '#6366f1' },
    emptyText: { fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: '20px 0' },
    
    // 로딩
    loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 },
    spinner: { width: 40, height: 40, border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    loadingText: { fontSize: 14, color: '#64748b' },
};

export default CalendarView;